import { Component, OnInit, inject, signal } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogClose,
} from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AxiosError } from 'axios';
import {
  RunTransition,
  RunTransitionWithDialog,
  WorkflowService,
} from 'src/app/shared/services/workflow/workflow.service';
import { errorResponse, ValidationErrors } from 'src/types/error-response';
import { PayeePopupComponent } from '../../payee-popup/payee-popup.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { ValidationErrorsComponent } from './validation-errors/validation-errors.component';
import { PayeeIconButtonComponent } from '../../payee-buttons/payee-icon-button/payee-icon-button.component';
import { PayeeIconComponent } from '../../payee-icon/payee-icon.component';
import {
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle,
} from '@angular/material/expansion';
import { PayeeButtonComponent } from '../../payee-buttons/payee-button/payee-button.component';
import { JsonPipe, KeyValuePipe } from '@angular/common';

type CompanyData = {
  basicData: {
    name: string;
    long_name: string;
    tax_number: string;
    tax_number_root: string;
    partyType: {
      label: string;
      type: string;
    } | null;
  };
  addressData: {
    street: string;
    settlement: string;
    postcode: string;
    country_iso: string;
  } | null;
  contactData: {
    type: string;
    value: string;
  } | null;
  bankAccountsData: {
    account_holder_name: string;
    iban: string | null;
    bban: string | null;
    bank_name: string | null;
  }[];
};

type ComparisonData = {
  database: CompanyData | null;
  company_search: CompanyData | null;
};

type DialogData = {
  rules: Record<string, string[]>;
  transition_label: string;
  debtor: ComparisonData;
  client: ComparisonData;
};

@Component({
  selector: 'app-generate-attorney-data-dialog',
  templateUrl: './generate-attorney-data-dialog.component.html',
  styleUrls: ['./generate-attorney-data-dialog.component.scss'],
  imports: [
    PayeePopupComponent,
    MatProgressSpinner,
    ValidationErrorsComponent,
    PayeeIconButtonComponent,
    PayeeIconComponent,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    PayeeButtonComponent,
    MatDialogClose,
    JsonPipe,
    KeyValuePipe,
  ],
})
export class GenerateAttorneyDataDialogComponent implements OnInit {
  private workflowService = inject(WorkflowService);
  private dialogRef =
    inject<MatDialogRef<GenerateAttorneyDataDialogComponent>>(MatDialogRef);
  data = inject<RunTransitionWithDialog>(MAT_DIALOG_DATA);
  private snackbar = inject(MatSnackBar);

  readonly loading = signal(false);
  validationErrors?: ValidationErrors;
  dialogData?: DialogData;

  async ngOnInit(): Promise<void> {
    await this.loadDialogData();
  }

  async loadDialogData(): Promise<void> {
    try {
      this.loading.set(true);
      this.dialogData = (await this.workflowService.getDialogData(
        this.data,
      )) as DialogData;
    } catch (error) {
      console.error('Failed to load dialog data:', error);
      this.snackbar.open('Hiba történt az adatok betöltése közben!', 'OK', {
        duration: 5000,
      });
      this.dialogRef.close();
    } finally {
      this.loading.set(false);
    }
  }

  async submit(): Promise<void> {
    if (this.loading()) {
      return;
    }

    try {
      this.loading.set(true);
      this.validationErrors = undefined;
      const payload: RunTransition = {
        caseId: this.data.caseId,
        transitionId: this.data.transitionId,
        params: this.data.extraParams,
      };
      await this.workflowService.runTransition(payload);
      this.dialogRef.close();
    } catch (error: AxiosError | unknown) {
      if (error instanceof AxiosError) {
        const errorResponseData = errorResponse.safeParse(error.response?.data);
        if (errorResponseData.success) {
          this.validationErrors = errorResponseData.data;
          return;
        }

        if (error.response?.data?.message) {
          this.snackbar.open(error.response.data.message, 'OK', {
            duration: 5000,
          });
          return;
        }
      }

      this.snackbar.open('Ismeretlen hiba!', 'OK', { duration: 5000 });
    } finally {
      this.loading.set(false);
    }
  }

  hasData(entity: ComparisonData): boolean {
    return !!(entity.database || entity.company_search);
  }

  getEntityName(entity: ComparisonData): string {
    if (entity.database?.basicData?.name) {
      return entity.database.basicData.name;
    }
    if (entity.company_search?.basicData?.name) {
      return entity.company_search.basicData.name;
    }
    return 'Ismeretlen';
  }

  // Generic comparison method for highlighting mismatches
  isMismatch<T>(
    entity: ComparisonData,
    getter: (data: CompanyData) => T,
  ): boolean {
    const dbValue = entity.database ? getter(entity.database) : null;
    const csValue = entity.company_search
      ? getter(entity.company_search)
      : null;
    return dbValue !== csValue;
  }

  // Specific comparison methods using the generic approach
  isNameMismatch(entity: ComparisonData): boolean {
    return this.isMismatch(entity, (data) => data.basicData?.name || '');
  }

  isLongNameMismatch(entity: ComparisonData): boolean {
    return this.isMismatch(entity, (data) => data.basicData?.long_name || '');
  }

  isTaxNumberMismatch(entity: ComparisonData): boolean {
    return this.isMismatch(entity, (data) => data.basicData?.tax_number || '');
  }

  isTaxNumberRootMismatch(entity: ComparisonData): boolean {
    return this.isMismatch(
      entity,
      (data) => data.basicData?.tax_number_root || '',
    );
  }

  isPartyTypeMismatch(entity: ComparisonData): boolean {
    return this.isMismatch(
      entity,
      (data) => data.basicData?.partyType?.label || '',
    );
  }

  isStreetMismatch(entity: ComparisonData): boolean {
    return this.isMismatch(entity, (data) => data.addressData?.street || '');
  }

  isSettlementMismatch(entity: ComparisonData): boolean {
    return this.isMismatch(
      entity,
      (data) => data.addressData?.settlement || '',
    );
  }

  isPostcodeMismatch(entity: ComparisonData): boolean {
    return this.isMismatch(entity, (data) => data.addressData?.postcode || '');
  }

  isContactMismatch(entity: ComparisonData): boolean {
    return this.isMismatch(entity, (data) => data.contactData?.value || '');
  }

  isBankAccountsMismatch(entity: ComparisonData): boolean {
    return this.isMismatch(entity, (data) => this.bankAccountsFingerprint(data));
  }

  isCountryIsoMismatch(entity: ComparisonData): boolean {
    return this.isMismatch(
      entity,
      (data) => data.addressData?.country_iso || '',
    );
  }

  private bankAccountsFingerprint(data: CompanyData): string {
    return (data.bankAccountsData ?? [])
      .map((account) => account.bban ?? account.iban ?? '')
      .sort()
      .join('|');
  }
}
